import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { getSession } from "@/lib/session";
import { trpc } from "@/lib/trpc";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PAGE_SHELL,
  CARD_SECTION,
  ACTION_BTN,
  GHOST_BTN,
  REQUEST_PILL,
  REQUEST_STATUS_LABEL,
  REQUEST_PROGRESS_COLOR,
} from "@/lib/ui-tokens";
import { useState } from "react";
import { EditRequestModal } from "@/components/modals";
import { toast } from "@/components/ui/toast";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DocumentViewer,
  ViewerShift,
  ViewButton,
  useDocumentViewer,
} from "@/components/modals/DocumentViewer";

export const Route = createFileRoute("/_app/requests/$requestId")({
  component: RequestDetailPage,
});

const REQUEST_UPLOAD_TYPE_LABELS: Record<string, string> = {
  pdf: "PDF",
  images: "Images",
  documents: "Word docs",
  spreadsheets: "Spreadsheets",
  csv: "CSV",
};

function readAllowedTypeLabels(raw: string | null | undefined): string[] {
  if (!raw) return Object.values(REQUEST_UPLOAD_TYPE_LABELS);
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed))
      return Object.values(REQUEST_UPLOAD_TYPE_LABELS);
    const labels = parsed
      .map((value) =>
        typeof value === "string" ? REQUEST_UPLOAD_TYPE_LABELS[value] : null,
      )
      .filter(Boolean) as string[];
    return labels.length > 0
      ? labels
      : Object.values(REQUEST_UPLOAD_TYPE_LABELS);
  } catch {
    return Object.values(REQUEST_UPLOAD_TYPE_LABELS);
  }
}

function labelForMimeType(mimeType: string | null | undefined): string {
  if (!mimeType) return "Document";
  if (mimeType === "application/pdf") return "PDF";
  if (mimeType.startsWith("image/")) return "Image";
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel"))
    return "Spreadsheet";
  if (mimeType.includes("csv")) return "CSV";
  if (mimeType.includes("word") || mimeType.includes("document"))
    return "Word doc";
  return "Document";
}

function getTimelineDotClass(eventType: string, payloadStr: string) {
  if (eventType === "request_sent") return "bg-(--green) border-(--green)";
  if (eventType === "nudge_sent") return "bg-(--accent) border-(--accent)";
  if (eventType === "doc_received") return "bg-(--blue) border-(--blue)";
  if (eventType === "request_created")
    return "bg-(--muted-light) border-(--muted-light)";

  if (eventType === "status_updated") {
    try {
      const payload = JSON.parse(payloadStr);
      const status = payload.status || payload.newStatus || "";
      switch (status) {
        case "approved":
          return "bg-(--green) border-(--green)";
        case "received":
          return "bg-(--blue) border-(--blue)";
        case "partial":
          return "bg-(--accent-light) border-(--accent-light)";
        case "pending":
          return "bg-(--accent) border-(--accent)";
        default:
          return "bg-(--muted-light) border-(--muted-light)";
      }
    } catch (e) {
      // fallback
    }
  }

  return "bg-(--border) border-(--border)";
}

function formatEventType(type: string) {
  switch (type) {
    case "request_sent":
      return "Request sent";
    case "nudge_sent":
      return "Reminder sent";
    case "doc_received":
      return "Document received";
    case "request_created":
      return "Request created";
    case "status_updated":
      return "Status updated";
    default:
      return type.replace("_", " ").toUpperCase();
  }
}

function formatEventPayload(type: string, payloadStr: string) {
  if (!payloadStr) return "";
  try {
    const payload = JSON.parse(payloadStr);
    switch (type) {
      case "request_sent":
        if (payload.channels) {
          return `Request delivered via ${payload.channels.map((c: string) => (c === "sms" ? "SMS" : c.charAt(0).toUpperCase() + c.slice(1))).join(" & ")}`;
        }
        return "Request sent to client";
      case "nudge_sent":
        return `Automated reminder sent via ${payload.channel === "sms" ? "SMS" : payload.channel || "email"}`;
      case "doc_received":
        return `Client uploaded: ${payload.fileName || "a document"}`;
      case "status_updated": {
        const newStatus = payload.status || payload.newStatus || "";
        const oldStatus = payload.oldStatus || "";
        const formatStatus = (s: string) =>
          s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

        if (oldStatus && newStatus) {
          return `Status changed from ${formatStatus(oldStatus)} to ${formatStatus(newStatus)}`;
        }
        if (newStatus) {
          return `Status updated to ${formatStatus(newStatus)}`;
        }
        return "Status updated";
      }
      default:
        return payload.message || payload.title || payloadStr;
    }
  } catch (e) {
    return payloadStr;
  }
}

// ── Page ──────────────────────────────────────────────────────────────────

function RequestDetailPage() {
  const { requestId } = useParams({ from: "/_app/requests/$requestId" });
  const session = getSession();
  const workspaceId = session?.workspaceId ?? "";
  const navigate = useNavigate();
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedSignature, setSelectedSignature] = useState<any | null>(null);
  const {
    viewerFile,
    open: openViewer,
    close: closeViewer,
    isOpen: viewerOpen,
  } = useDocumentViewer();

  const request_q = trpc.requests.get.useQuery(
    { id: requestId },
    { enabled: Boolean(workspaceId && requestId) },
  );

  const docs_q = trpc.documents.list.useQuery(
    { requestId },
    { enabled: Boolean(workspaceId && requestId) },
  );

  const utils = trpc.useUtils();
  const deleteRequest = trpc.requests.delete.useMutation({
    onSuccess() {
      toast.success("Request deleted");
      navigate({ to: "/requests" });
      utils.requests.list.invalidate();
    },
    onError(err) {
      toast.error(err.message || "Failed to delete request");
    },
  });

  const approveRequest = trpc.requests.update.useMutation({
    onSuccess() {
      toast.success("Request approved");
      utils.requests.get.invalidate({ id: requestId });
      utils.requests.list.invalidate();
    },
    onError(err) {
      toast.error(err.message || "Failed to approve request");
    },
  });

  const sendReminder = trpc.requests.send.useMutation({
    onSuccess() {
      toast.success("Reminder sent successfully");
      utils.requests.get.invalidate({ id: requestId });
    },
    onError(err) {
      toast.error(err.message || "Failed to send reminder");
    },
  });

  const isLoading = request_q.isLoading || docs_q.isLoading;
  const data = request_q.data;
  const r = data?.request;
  const clientName = data?.clientName;
  const clientId = r?.clientId || "";
  const items = data?.items ?? [];
  const events = data?.events ?? [];
  const sigs = data?.sigs ?? [];
  const docs = docs_q.data ?? [];

  const docsReceived = items.filter(
    (i) => i.status === "received" || i.status === "approved",
  ).length;
  const docsTotal = items.length;
  const pct = docsTotal > 0 ? (docsReceived / docsTotal) * 100 : 0;
  const requestFulfilled = r?.status === "received" || r?.status === "approved";

  if (isLoading) {
    return (
      <div className={PAGE_SHELL}>
        <Skeleton className="h-3.5 w-20" />
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-6 w-64" />
            <Skeleton className="h-3.5 w-48" />
          </div>
          <Skeleton className="h-8 w-28" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="border border-(--border) bg-card px-3.5 py-3 space-y-2"
            >
              <Skeleton className="h-2.5 w-16" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
        <div className={CARD_SECTION}>
          <div className="px-4 py-2.5 border-b border-(--border)">
            <Skeleton className="h-3 w-24" />
          </div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="px-4 py-3 flex gap-3">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (!r) return <div className={PAGE_SHELL}>Request not found</div>;

  return (
    <ViewerShift isOpen={viewerOpen}>
      <div className={PAGE_SHELL}>
        {/* Back link */}
        <button
          type="button"
          onClick={() => window.history.back()}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M6 2L3 5l3 3"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="square"
              strokeLinejoin="miter"
            />
          </svg>
          Back
        </button>

        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <h1 className="text-[16px] font-bold tracking-tight text-foreground leading-snug truncate">
                {r.title}
              </h1>
              <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                <span>
                  <Link
                    to={`/clients/$clientId`}
                    params={{ clientId: String(clientId) }}
                    search={{ tab: "overview" }}
                    className="hover:text-foreground transition-colors underline underline-offset-2"
                  >
                    {clientName}
                  </Link>
                </span>
                · Created {new Date(r.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <span
              className={`inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 ${REQUEST_PILL[r.status]}`}
            >
              {REQUEST_STATUS_LABEL[r.status]}
            </span>
            {!requestFulfilled && (
              <button
                onClick={() => sendReminder.mutate({ requestId })}
                disabled={sendReminder.isPending}
                className={GHOST_BTN}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M11 1L1 5.5l4 1.5L7 11l4-10z"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                  />
                </svg>
                {sendReminder.isPending ? "Sending…" : "Send reminder"}
              </button>
            )}
            {r.status === "approved" ? null : requestFulfilled ? (
              <button
                onClick={() =>
                  approveRequest.mutate({ id: requestId, status: "approved" })
                }
                disabled={approveRequest.isPending}
                className={ACTION_BTN}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6l3 3 5-5"
                    stroke="currentColor"
                    strokeWidth="1.3"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                  />
                </svg>
                {approveRequest.isPending ? "Approving…" : "Approve all"}
              </button>
            ) : (
              <button onClick={() => setShowEdit(true)} className={GHOST_BTN}>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 2h8v8H2V2zM2 5h8M2 8h8"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="square"
                    strokeLinejoin="miter"
                  />
                </svg>
                Edit
              </button>
            )}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 h-8 text-[11px] font-bold text-(--warn) hover:bg-(--colors-red-50) transition-colors border border-transparent hover:border-(--colors-red-200)"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 3h8M3 3v8h6V3M4 3V1.5h4V3M4.5 5.5v3M7.5 5.5v3"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="square"
                  strokeLinejoin="miter"
                />
              </svg>
              Delete
            </button>
          </div>
        </div>

        <AlertDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
        >
          <AlertDialogContent className="rounded-none border-2 border-(--border) max-w-[400px]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[16px] font-bold tracking-tight">
                Delete request?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[12px] leading-relaxed">
                Are you sure you want to delete this request? This will
                permanently remove all associated documents and history. This
                action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-6">
              <AlertDialogCancel className="rounded-none h-9 text-[12px] font-bold">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteRequest.mutate({ id: requestId })}
                className="rounded-none h-9 text-[12px] font-bold bg-(--warn)  hover:bg-(--warn)/90 border-none"
              >
                Delete request
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Meta strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <div className="border border-(--border) bg-card px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              Due date
            </p>
            <p
              className={`text-[13px] font-bold mt-1 ${r.status === "pending" && r.dueDate && new Date(r.dueDate) < new Date() ? "text-(--warn)" : "text-foreground"}`}
            >
              {r.dueDate
                ? new Date(r.dueDate).toLocaleDateString()
                : "No due date"}
            </p>
          </div>
          <div className="border border-(--border) bg-card px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              Progress
            </p>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="w-12 h-1.5 bg-(--border) overflow-hidden">
                <div
                  className="h-full"
                  style={{
                    width: `${pct}%`,
                    background: REQUEST_PROGRESS_COLOR[r.status],
                  }}
                />
              </div>
              <span className="text-[11px] font-bold text-foreground tabular-nums">
                {docsReceived}/{docsTotal}
              </span>
            </div>
          </div>
          <div className="border border-(--border) bg-card px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              Reminder
            </p>
            <p className="text-[13px] font-bold mt-1 text-foreground">Active</p>
          </div>
          <div className="border border-(--border) bg-card px-3.5 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
              Last activity
            </p>
            <p className="text-[13px] font-bold mt-1 text-foreground">
              {new Date(r.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Description */}
        {r.description && (
          <div className="border border-(--border) bg-card px-4 py-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-1.5">
              Instructions
            </p>
            <p className="text-[12px] text-foreground leading-relaxed">
              {r.description}
            </p>
          </div>
        )}

        {/* Documents */}
        <div className={CARD_SECTION}>
          <div className="px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
              Items ({docsReceived}/{docsTotal})
            </p>
          </div>
          <div className="divide-y divide-(--border)">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex flex-col border-b border-(--border) last:border-0 hover:bg-(--surface-hover) transition-colors group"
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center border text-[9px] font-bold ${
                      item.status === "received" || item.status === "approved"
                        ? "border-(--colors-green-200) bg-(--green-bg) text-(--green)"
                        : "border-(--border) bg-(--muted-token) text-muted-foreground"
                    }`}
                  >
                    {item.status === "received" ||
                    item.status === "approved" ? (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <path
                          d="M3 6l2 2 4-4"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="square"
                          strokeLinejoin="miter"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                      >
                        <circle cx="6" cy="6" r="1.5" fill="currentColor" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-[12px] font-semibold truncate ${item.status === "received" || item.status === "approved" ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      {item.label}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {item.status === "approved"
                        ? "Approved"
                        : item.status === "received"
                          ? "Received"
                          : "Awaiting upload"}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(item.status === "received" || item.status === "approved"
                        ? Array.from(
                            new Set(
                              docs
                                .filter(
                                  (d) => (d as any).requestItemId === item.id,
                                )
                                .map((d) =>
                                  labelForMimeType((d as any).mimeType),
                                ),
                            ),
                          )
                        : readAllowedTypeLabels((item as any).allowedFileTypes)
                      ).map((label) => (
                        <span
                          key={label}
                          className="inline-flex items-center border border-(--border) bg-(--muted-token) px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground"
                        >
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {docs.filter((d) => (d as any).requestItemId === item.id)
                  .length > 0 && (
                  <div className="px-4 pb-3 pl-13 flex flex-col gap-2">
                    {docs
                      .filter((d) => (d as any).requestItemId === item.id)
                      .map((d) => (
                        <div
                          key={d.id}
                          className="flex items-center justify-between gap-3 p-2 border border-(--border) bg-card"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center bg-(--muted-token) text-muted-foreground text-[7px] font-bold">
                              DOC
                            </div>
                            <p className="text-[11px] font-medium text-foreground truncate">
                              {d.fileName}
                            </p>
                          </div>
                          {/*<a href={`/upload/download/${ d.id }`} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-muted-foreground hover:text-foreground border border-transparent hover:border-(--border) px-1.5 py-0.5 transition-all">Download</a>*/}
                          <div className="flex items-center gap-1.5">
                            <ViewButton
                              file={{ id: d.id, fileName: d.fileName }}
                              onView={openViewer}
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Uploaded Files */}

        {/* Signed Signatures */}
        {sigs.length > 0 && (
          <div className={CARD_SECTION}>
            <div className="px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100) flex items-center justify-between">
              <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
                Completed Signatures ({sigs.length})
              </p>
            </div>
            <div className="divide-y divide-(--border)">
              {sigs.map((sig) => (
                <div
                  key={sig.id}
                  className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-(--surface-hover) transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-(--border) bg-(--green-bg) text-(--green) text-[8px] font-bold">
                      SIG
                    </div>
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-foreground truncate">
                        Signature on {sig.documentName}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Signed by {clientName} ·{" "}
                        {sig.signedAt
                          ? new Date(sig.signedAt).toLocaleDateString()
                          : "—"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedSignature(sig)}
                    className="flex items-center gap-1.5 border border-border bg-card text-[10px] font-bold text-muted-foreground px-2.5 h-7 hover:text-foreground hover:bg-muted/50 transition-colors rounded-none"
                  >
                    View Signature
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Internal Notes & Communication */}
        <div className={CARD_SECTION}>
          <div className="px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100) flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
              Communication & Notes
            </p>
          </div>

          <div className="p-4">
            <div className="relative ml-2.5">
              {/* Vertical line */}
              <div className="absolute left-0 top-1 bottom-1 w-px bg-(--border)" />
              <div className="space-y-5">
                {/* Standard Progression Steps */}
                <div className="flex items-start gap-3 relative pl-5">
                  <div className="absolute left-[-3.5px] top-1.5 w-[7px] h-[7px] border-2 bg-card bg-(--green) border-(--green)" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold text-foreground">
                      Request Created
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 relative pl-5 opacity-80 hover:opacity-100 transition-opacity">
                  <div
                    className={`absolute left-[-3.5px] top-1.5 w-[7px] h-[7px] border-2 bg-card ${events.some((e) => e.eventType === "request_sent") ? "bg-(--green) border-(--green)" : "bg-(--muted-token) border-(--border)"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-[11px] font-bold ${events.some((e) => e.eventType === "request_sent") ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      Request Sent
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 relative pl-5 opacity-80 hover:opacity-100 transition-opacity">
                  <div
                    className={`absolute left-[-3.5px] top-1.5 w-[7px] h-[7px] border-2 bg-card ${r.status === "received" || r.status === "approved" ? "bg-(--blue) border-(--blue)" : "bg-(--muted-token) border-(--border)"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-[11px] font-bold ${r.status === "received" || r.status === "approved" ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      Documents Received
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 relative pl-5 opacity-80 hover:opacity-100 transition-opacity">
                  <div
                    className={`absolute left-[-3.5px] top-1.5 w-[7px] h-[7px] border-2 bg-card ${r.status === "approved" ? "bg-(--green) border-(--green)" : "bg-(--muted-token) border-(--border)"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-[11px] font-bold ${r.status === "approved" ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      Documents Approved
                    </p>
                  </div>
                </div>

                {events.length > 0 && (
                  <div className="my-4 pt-4 border-t border-(--border)" />
                )}

                {/* Real Timeline */}
                {events.map((ev) => (
                  <div
                    key={ev.id}
                    className="flex items-start gap-3 relative pl-5 opacity-80 hover:opacity-100 transition-opacity"
                  >
                    <div
                      className={`absolute left-[-3.5px] top-1.5 w-[7px] h-[7px] border-2 bg-card ${getTimelineDotClass(ev.eventType, ev.payload || "")}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] font-bold text-foreground">
                        {formatEventType(ev.eventType)}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed">
                        {formatEventPayload(ev.eventType, ev.payload || "")}
                      </p>
                    </div>
                    <span className="text-[9px] font-medium text-muted-foreground/60 shrink-0 whitespace-nowrap">
                      {new Date(ev.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <EditRequestModal
          open={showEdit}
          onClose={() => setShowEdit(false)}
          request={
            r
              ? {
                  ...r,
                  items: items.map((item) => ({
                    id: item.id,
                    label: item.label,
                    allowedFileTypes: (() => {
                      try {
                        return typeof item.allowedFileTypes === "string"
                          ? JSON.parse(item.allowedFileTypes)
                          : item.allowedFileTypes;
                      } catch {
                        return [];
                      }
                    })(),
                  })),
                }
              : null
          }
        />

        {/* Signature Audit Dialog */}
        <Dialog
          open={!!selectedSignature}
          onOpenChange={(open) => !open && setSelectedSignature(null)}
        >
          <DialogContent className="max-w-[480px] rounded-none border-2 border-(--border) p-5">
            <DialogHeader className="mb-4">
              <DialogTitle className="text-[14px] font-bold">
                Signature Audit Details
              </DialogTitle>
              <DialogDescription className="text-[10px]">
                Legally binding electronic signature collected via the secure
                Client Portal.
              </DialogDescription>
            </DialogHeader>

            {selectedSignature && (
              <div className="space-y-4">
                {/* The Signature Image / text */}
                <div className="border border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100) p-4 flex items-center justify-center min-h-[140px]">
                  {selectedSignature.signatureData ? (
                    selectedSignature.signatureData.startsWith(
                      "data:image/",
                    ) ? (
                      <img
                        src={selectedSignature.signatureData}
                        alt={`Signature by ${clientName}`}
                        className="max-h-[100px] object-contain dark:invert"
                      />
                    ) : (
                      <span className="text-[20px] font-bold italic tracking-wider font-serif px-4 py-2 border border-dashed border-(--border) bg-card text-foreground">
                        {selectedSignature.signatureData}
                      </span>
                    )
                  ) : (
                    <p className="text-[11px] text-muted-foreground italic">
                      No drawing data
                    </p>
                  )}
                </div>

                {/* Audit Metadata Table */}
                <div className="border border-(--border) divide-y divide-(--border) text-[11px]">
                  <div className="grid grid-cols-[100px_1fr] p-2 bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
                    <span className="font-bold text-muted-foreground">
                      Signatory
                    </span>
                    <span className="font-semibold text-foreground">
                      {clientName}
                    </span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] p-2">
                    <span className="font-bold text-muted-foreground">
                      Document
                    </span>
                    <span className="font-semibold text-foreground">
                      {selectedSignature.documentName}
                    </span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] p-2 bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
                    <span className="font-bold text-muted-foreground">
                      Signed Date
                    </span>
                    <span className="font-semibold text-foreground">
                      {selectedSignature.signedAt
                        ? new Date(selectedSignature.signedAt).toLocaleString()
                        : "—"}
                    </span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] p-2">
                    <span className="font-bold text-muted-foreground">
                      IP Address
                    </span>
                    <span className="font-semibold text-foreground tabular-nums">
                      {selectedSignature.ipAddress || "Unknown"}
                    </span>
                  </div>
                  <div className="grid grid-cols-[100px_1fr] p-2 bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
                    <span className="font-bold text-muted-foreground">
                      Signature ID
                    </span>
                    <span className="font-semibold text-foreground tabular-nums select-all">
                      {selectedSignature.id}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
      <DocumentViewer file={viewerFile} onClose={closeViewer} />
    </ViewerShift>
  );
}

// {docs.length > 0 && (
//     <div className={CARD_SECTION}>
//         <div className="px-4 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100) flex items-center justify-between">
//             <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-foreground">
//                 Uploaded Files ({docs.length})
//             </p>
//         </div>
//         <div className="divide-y divide-(--border)">
//             {docs.map( doc => (
//                 <div key={doc.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-(--surface-hover) transition-colors group">
//                     <div className="flex items-center gap-3 min-w-0">
//                         <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-(--border) bg-(--muted-token) text-muted-foreground text-[8px] font-bold">
//                             DOC
//                         </div>
//                         <div className="min-w-0">
//                             <p className="text-[12px] font-semibold text-foreground truncate">
//                                 {doc.fileName}
//                             </p>
//                             <p className="text-[10px] text-muted-foreground mt-0.5">
//                                 {( doc.fileSize / 1024 ).toFixed( 1 )} KB · Received {new Date( doc.createdAt ).toLocaleDateString()}
//                             </p>
//                         </div>
//                     </div>

//                     <div className="flex items-center gap-1.5">
//                              <ViewButton
//                                file={{
//                                  id:        doc.id,
//                                  fileName:  doc.fileName,
//                                  fileSize:  doc.fileSize,
//                                  createdAt: doc.createdAt,
//                                }}
//                                onView={openViewer}
//                              />

//                            </div>
//                 </div>
//             ) )}
//         </div>
//     </div>
// )}
